import React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  TextInput,
  TextAreaInput,
  DateTimeInput,
  SelectInput,
} from "../../react-hook-form-inputs";
import { renderRailsErrors } from "../../../utils/rails_error_parser";
import {
  parseDate,
} from "./../../../utils/datetime";
import { PortalizedModalContext } from "./../../../components/PortalizedModal";
import { liveEventApi } from './api';

const FormSchema = Yup.object().shape({
  title: Yup.string().required(),
  description: Yup.string().required(),
  status: Yup.string().oneOf(['upcoming', 'live', 'completed']),
  youtube_url: Yup.string().required().url('enter valid youtube url'),
  scheduled_time: Yup.date().required()
});

function transformFieldValues(data, forForm = false){
  const values = {
    title: data.title,
    description: data.description,
    status: data.status,
    scheduled_time: data.scheduled_time,
    youtube_url: data.youtube_url
  };

  if(forForm){
    values.scheduled_time = parseDate(values.scheduled_time);
  }

  return values;
}

export default function Form({ offerId, eventId, onSuccess }) {
  const queryClient = useQueryClient();
  const confirmModal = React.useContext( PortalizedModalContext );

  const { data:liveEvent, isLoading } = useQuery(
    ['live-event', eventId],
    () => {
      return liveEventApi.fetch(eventId);
    }, 
    {
      enabled: eventId !== null && eventId !== undefined,
      initialData: {}
    }
  );

  const isEditForm = liveEvent.id;

  const defaultValues = {
    title: '',
    description: '',
    status: 'upcoming',
    scheduled_time: null,
    youtube_url: ''
  };

  const methods = useForm({
    resolver: yupResolver(FormSchema),
    mode: 'all',
    reValidateMode: 'all',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
    defaultValues: defaultValues
  });

  React.useEffect( 
    () => {
      if(liveEvent){
        methods.reset(transformFieldValues(liveEvent, true));
      }
    },
    [liveEvent, methods]
  )

  const saveLiveEvent = useMutation(
    isEditForm ? liveEventApi.update : liveEventApi.create,
    {
      onSuccess: (values) => {
        queryClient.invalidateQueries(['live-events-list', values.offer_id]);
        toast.success(
          `Live event successfully ${liveEvent ? 'updated' : 'added'}.`
        );
        if (typeof onSuccess === "function") onSuccess()
      },
      onError: (error) => {
        renderRailsErrors(error);
      },
    }
  )

  const onSubmit = data => {
    confirmModal.setModalContent(
      null,
      <div>
        <div className="px-3">
          <p>{`Are you sure you want to ${isEditForm ? "save changes to" : "create"} the live event?`}</p>
        </div>
        <div className="p-3 text-right">
          <button
            className="btn btn-dark rounded-0 px-3"
            onClick={() => {
              saveLiveEvent.mutate({
                id: liveEvent.id || offerId,
                attrs: transformFieldValues(data)
              })
              confirmModal.closeModal()
            }}
          >
            Yes, {isEditForm ? "save changes to" : "create"} the live event
          </button>
        </div>
      </div>
    )
    confirmModal.openModal()
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="mt-1" autoComplete="off">
        <div className="row mt-3">
          <div className="col input-bg-white">
            <TextInput name="title" placeholder="Title"/>
          </div>
        </div>
        <div className="row">
          <div className="col input-bg-white">
            <TextAreaInput name="description" placeholder="Description" rows={5}/>
          </div>
        </div>
        <div className="row">
          <div className="col-6 input-bg-white">
            <DateTimeInput name="scheduled_time" placeholder="Scheduled Time"/>
          </div>
          <div className="col-6 input-bg-white">
            <SelectInput
              name="status"
              placeholder="Status"
              options={['upcoming', 'live', 'completed']}
              labelClass="z-index-1000"
              className="ml-1"
            />
          </div>
        </div>
        <div className="row">
          <div className="col input-bg-white">
            <TextInput name="youtube_url" placeholder="Youtube embed URL"/>
          </div>
        </div>
        <div className="form-group mt-3">
          <button type="submit"
            className="mx-auto zeitung-mono weight-300 btn btn-outline-dark rounded-0"
            disabled={methods.isSubmitting}
          >
            { isEditForm ? 'Update' : 'Add' }
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
